import { sanitizeCmsHtml } from '@shared/helpers/sanitize-cms-html';
import { animate, AnimationBuilder, AnimationPlayer, style } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/** Czas animacji rozwinięcia / zwinięcia bloku (ms) */
const EXPAND_COLLAPSE_DURATION_MS = 400;

@Component({
  selector: 'app-text-collapse',
  templateUrl: './text-collapse.component.html',
  styleUrls: ['./text-collapse.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class TextCollapseComponent implements OnInit, OnDestroy {
  @Input() content: string = '';
  @Input() maxLength: number = 0;

  /** Treść po sanityzacji — jedyna wersja, której wolno użyć do renderowania. */
  private safeContent = '';

  @ViewChild('clipWrapper', { read: ElementRef }) clipWrapper?: ElementRef<HTMLElement>;
  @ViewChild('contentHost', { read: ElementRef }) contentHost?: ElementRef<HTMLElement>;

  isExpanded = false;
  /** Aktualnie renderowany fragment HTML (skrócony lub pełny) */
  displayContent: SafeHtml = '';
  truncatedContent: SafeHtml = '';
  fullContent: SafeHtml = '';
  showToggle = false;

  truncatedLength = 0;
  fullTextLength = 0;

  /** Blokuje podwójne kliknięcia w trakcie animacji */
  animationInProgress = false;

  private clipPlayer: AnimationPlayer | null = null;
  private animationDoneFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private animationBuilder: AnimationBuilder,
    private ngZone: NgZone,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit(): void {
    this.processContent();
    this.applyInitialDisplay();
  }

  ngOnDestroy(): void {
    this.destroyClipPlayer();
    this.clearDoneFallback();
  }

  private applyInitialDisplay(): void {
    if (!this.showToggle) {
      this.displayContent = this.fullContent;
    } else {
      this.displayContent = this.truncatedContent;
    }
  }

  private processContent(): void {
    // AT-9147: treść idzie przez DOMPurify ZANIM trafi do `bypassSecurityTrustHtml` i do
    // tymczasowych elementów `innerHTML` (te też wykonują `onerror`, choć są odpięte od DOM).
    this.safeContent = sanitizeCmsHtml(this.content);

    if (!this.safeContent || !this.maxLength || this.maxLength <= 0) {
      this.fullContent = this.sanitizer.bypassSecurityTrustHtml(this.safeContent);
      this.truncatedContent = this.fullContent;
      this.showToggle = false;
      return;
    }

    this.fullContent = this.sanitizer.bypassSecurityTrustHtml(this.safeContent);
    this.fullTextLength = this.getTextLength(this.safeContent);

    if (this.fullTextLength <= this.maxLength) {
      this.truncatedContent = this.fullContent;
      this.truncatedLength = this.fullTextLength;
      this.showToggle = false;
      return;
    }

    this.showToggle = true;
    const truncatedHtml = this.truncateHtmlContent(this.safeContent, this.maxLength);
    this.truncatedContent = this.sanitizer.bypassSecurityTrustHtml(truncatedHtml);
    this.truncatedLength = this.getTextLength(truncatedHtml);
  }

  private getTextLength(html: string): number {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.trim().length || 0;
  }

  private truncateHtmlContent(html: string, maxLength: number, exactLength: boolean = false): string {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const textContent = tempDiv.textContent || '';
    const textLength = textContent.trim().length;

    if (textLength <= maxLength) {
      return html;
    }

    let cutPosition = maxLength;
    if (!exactLength) {
      if (cutPosition < textLength) {
        const charAtPosition = textContent[cutPosition];
        if (charAtPosition && !/\s/.test(charAtPosition) && !/[.,!?;:]/.test(charAtPosition)) {
          let endOfWord = cutPosition;
          while (endOfWord < textLength && !/\s/.test(textContent[endOfWord])) {
            endOfWord++;
          }
          cutPosition = endOfWord;
        }
      }
    }

    const truncatedText = textContent.substring(0, cutPosition).trim();
    const truncatedTextLength = truncatedText.length;

    return this.truncateHtmlPreservingStructure(tempDiv, truncatedTextLength, exactLength);
  }

  private truncateHtmlPreservingStructure(
    element: HTMLElement,
    maxTextLength: number,
    exactLength: boolean = false,
  ): string {
    const cloned = element.cloneNode(true) as HTMLElement;
    const walker = document.createTreeWalker(cloned, NodeFilter.SHOW_TEXT, null);

    let currentLength = 0;
    let foundCutPoint = false;

    while (walker.nextNode() && !foundCutPoint) {
      const textNode = walker.currentNode as Text;
      const text = textNode.textContent || '';
      const textLength = text.trim().length;

      if (currentLength + textLength >= maxTextLength) {
        const keepLength = maxTextLength - currentLength;
        let finalKeepLength = keepLength;

        if (!exactLength && keepLength < text.length) {
          const charAtPosition = text[keepLength];
          if (charAtPosition && !/\s/.test(charAtPosition) && !/[.,!?;:]/.test(charAtPosition)) {
            let endOfWord = keepLength;
            while (endOfWord < text.length && !/\s/.test(text[endOfWord])) {
              endOfWord++;
            }
            finalKeepLength = endOfWord;
          }
        }

        textNode.textContent = text.substring(0, finalKeepLength).trim() + '\u00A0...';

        let nextNode = textNode.nextSibling;
        while (nextNode) {
          const toRemove = nextNode;
          nextNode = nextNode.nextSibling;
          toRemove.remove();
        }

        let parent = textNode.parentNode;
        while (parent && parent !== cloned) {
          let sibling = parent.nextSibling;
          while (sibling) {
            const toRemove = sibling;
            sibling = sibling.nextSibling;
            toRemove.remove();
          }
          parent = parent.parentNode;
        }

        foundCutPoint = true;
        break;
      }

      currentLength += textLength;
    }

    return cloned.innerHTML;
  }

  private prefersReducedMotion(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return true;
    }
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  }

  private instantToggle(): void {
    this.isExpanded = !this.isExpanded;
    this.displayContent = this.isExpanded ? this.fullContent : this.truncatedContent;
    this.cdr.markForCheck();
  }

  toggleExpanded(): void {
    if (!this.showToggle || this.animationInProgress) {
      return;
    }

    if (!isPlatformBrowser(this.platformId)) {
      this.instantToggle();
      return;
    }

    if (this.prefersReducedMotion()) {
      this.instantToggle();
      return;
    }

    const wrapper = this.clipWrapper?.nativeElement;
    const host = this.contentHost?.nativeElement;
    if (!wrapper || !host) {
      this.instantToggle();
      return;
    }

    if (this.isExpanded) {
      this.runCollapseAnimation(wrapper, host);
    } else {
      this.runExpandAnimation(wrapper, host);
    }
  }

  private runExpandAnimation(wrapper: HTMLElement, host: HTMLElement): void {
    this.destroyClipPlayer();
    this.clearDoneFallback();

    this.animationInProgress = true;
    this.isExpanded = true;
    this.cdr.markForCheck();

    const startHeight = Math.round(wrapper.getBoundingClientRect().height);
    wrapper.style.overflow = 'hidden';
    wrapper.style.height = `${startHeight}px`;

    this.displayContent = this.fullContent;
    this.cdr.detectChanges();

    const endHeight = Math.max(Math.round(host.scrollHeight), startHeight);

    if (endHeight === startHeight) {
      this.finishClipAnimation(wrapper, false);
      return;
    }

    this.clipPlayer = this.animationBuilder
      .build([
        style({ height: `${startHeight}px`, overflow: 'hidden' }),
        animate(
          `${EXPAND_COLLAPSE_DURATION_MS}ms cubic-bezier(0.33, 1, 0.68, 1)`,
          style({ height: `${endHeight}px`, overflow: 'hidden' }),
        ),
      ])
      .create(wrapper);

    this.scheduleAnimationDoneFallback(wrapper, false);
    this.clipPlayer.onDone(() => {
      this.ngZone.run(() => this.finishClipAnimation(wrapper, false));
    });
    this.clipPlayer.play();
  }

  /**
   * Wysokość układu skróconego HTML — bez podmiany treści w widoku.
   * Probe w tym samym wrapperze co host (szerokość + dziedziczenie stylów jak pełna treść),
   * zamiast body+fixed — unikamy endHeight >> startHeight i natychmiastowego skip animacji.
   */
  private measureTruncatedLayoutHeightPx(host: HTMLElement, wrapper: HTMLElement): number {
    const html = this.truncateHtmlContent(this.safeContent, this.maxLength);
    const probe = document.createElement('div');
    probe.className = host.className;
    probe.setAttribute('aria-hidden', 'true');
    probe.style.cssText = [
      'visibility:hidden',
      'pointer-events:none',
      'position:absolute',
      'left:0',
      'top:0',
      'width:100%',
      'height:auto',
      'overflow:hidden',
      'box-sizing:border-box',
    ].join(';');

    const cs = getComputedStyle(host);
    probe.style.fontFamily = cs.fontFamily;
    probe.style.fontSize = cs.fontSize;
    probe.style.fontWeight = cs.fontWeight;
    probe.style.fontStyle = cs.fontStyle;
    probe.style.lineHeight = cs.lineHeight;
    probe.style.letterSpacing = cs.letterSpacing;
    probe.style.wordSpacing = cs.wordSpacing;
    probe.style.textAlign = cs.textAlign;
    probe.style.padding = cs.padding;
    probe.style.margin = cs.margin;
    probe.style.border = cs.border;

    probe.innerHTML = html;
    wrapper.appendChild(probe);
    const h = Math.max(Math.ceil(probe.offsetHeight), 1);
    wrapper.removeChild(probe);
    return h;
  }

  private runCollapseAnimation(wrapper: HTMLElement, host: HTMLElement): void {
    this.destroyClipPlayer();
    this.clearDoneFallback();

    this.animationInProgress = true;
    /** isExpanded zostaje true do końca animacji — etykieta/aria zgodne z pełną treścią podczas zwijania */
    this.cdr.markForCheck();

    const startHeight = Math.max(Math.round(host.scrollHeight), Math.round(wrapper.offsetHeight));
    wrapper.style.overflow = 'hidden';
    wrapper.style.height = `${startHeight}px`;
    /** Pełna treść zostaje w DOM — overflow obcina od dołu podczas animacji wysokości */

    let endHeight = this.measureTruncatedLayoutHeightPx(host, wrapper);

    /** Skrócona treść musi być niższa niż pełna — jeśli pomiar nadal >= start, to błąd pomiaru (np. stary probe na body). */
    if (endHeight >= startHeight && this.showToggle && this.truncatedLength < this.fullTextLength) {
      endHeight = Math.max(1, Math.floor(startHeight * 0.35));
    }

    if (endHeight >= startHeight) {
      this.displayContent = this.truncatedContent;
      this.cdr.detectChanges();
      this.finishClipAnimation(wrapper, true);
      return;
    }

    this.clipPlayer = this.animationBuilder
      .build([
        style({ height: `${startHeight}px`, overflow: 'hidden' }),
        animate(
          `${EXPAND_COLLAPSE_DURATION_MS}ms cubic-bezier(0.32, 0, 0.67, 0)`,
          style({ height: `${endHeight}px`, overflow: 'hidden' }),
        ),
      ])
      .create(wrapper);

    this.scheduleAnimationDoneFallback(wrapper, true);
    this.clipPlayer.onDone(() => {
      this.ngZone.run(() => {
        this.displayContent = this.truncatedContent;
        this.cdr.detectChanges();
        this.finishClipAnimation(wrapper, true);
      });
    });
    this.clipPlayer.play();
  }

  /**
   * @param endCollapsed — po zwinięciu: ustaw isExpanded = false (etykieta „Pokaż więcej” dopiero po animacji).
   */
  private finishClipAnimation(wrapper: HTMLElement, endCollapsed = false): void {
    this.clearDoneFallback();
    this.destroyClipPlayer();
    wrapper.style.height = '';
    wrapper.style.overflow = '';
    wrapper.style.maxHeight = '';
    if (endCollapsed) {
      this.isExpanded = false;
    }
    this.animationInProgress = false;
    this.cdr.markForCheck();
  }

  private destroyClipPlayer(): void {
    if (this.clipPlayer) {
      try {
        this.clipPlayer.destroy();
      } catch {
        /* ignore */
      }
      this.clipPlayer = null;
    }
  }

  private scheduleAnimationDoneFallback(wrapper: HTMLElement, endCollapsed = false): void {
    this.clearDoneFallback();
    this.animationDoneFallbackTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (endCollapsed) {
          this.displayContent = this.truncatedContent;
          this.cdr.detectChanges();
        }
        this.finishClipAnimation(wrapper, endCollapsed);
      });
    }, EXPAND_COLLAPSE_DURATION_MS + 100);
  }

  private clearDoneFallback(): void {
    if (this.animationDoneFallbackTimer !== null) {
      clearTimeout(this.animationDoneFallbackTimer);
      this.animationDoneFallbackTimer = null;
    }
  }
}
